import _extends from '@babel/runtime/helpers/extends';
import React__default, { useCallback, useLayoutEffect } from 'react';
import { P as PopoverContentContainer } from '../Popover/PopoverContentContainer/PopoverContentContainer-DA7FAJfA.js';
import { P as PopoverContainer } from '../Popover/PopoverContainer-Bu6MTiRK.js';
import { u as useInlinePopover } from '../Popover/useInlinePopover-D_iJ07xC.js';
import { P as PopoverContentContainerWrapper, O as OverflowWrapper } from './Dropdown.styles-DdZUZjp1.js';
import { i as isEventFromWithinElement } from '../LocalModules/storm-ui-utils/dist/esm/isEventFromWithinElement-BvBeBwSW-BvBeBwSW.js';
import { t as trapTabFocus } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/trapTabFocus-CDT7jWDj-DPG2qBEI.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-CVa_vjg3-CVa_vjg3.js';
import { i as isFunction } from '../LocalModules/storm-ui-utils/dist/esm/isFunction-DjxTmSal-DjxTmSal.js';
import { f as focusFirstElement } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/focusFirstElement-C1TqGpwr-LXfVb-eG.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

const DropdownPopover = ({
  listboxProps,
  isOpen,
  onClose,
  offsetDistance = 0,
  offsetSkidding = 0,
  placement = 'bottom-start',
  enableFlip = true,
  $minWidth,
  $fullWidth,
  maxHeight,
  onKeyDown,
  popoverElementDidMount,
  popoverRef,
  children,
  shadowRoot,
  trigger,
  focusFirstElementOnPopoverExited = false,
  closePopoverIfTriggerClicked = false
}) => {
  const {
    attributes,
    styles,
    triggerElement,
    popoverElement,
    setPopoverElement,
    setTriggerElement,
    update
  } = useInlinePopover({
    offsetDistance,
    offsetSkidding,
    placement,
    enableFlip,
    preventOverflow: {
      mainAxis: true
    },
    boundaryRef: undefined,
    strategy: 'absolute'
  });

  /**
   * Click on outside popover and trigger should close the popover
   */
  const handleDocumentMousedown = useCallback(event => {
    if (typeof onClose === 'function') {
      const isNotWithinPopover = !isEventFromWithinElement(event, popoverElement);
      const isNotWithinTrigger = !isEventFromWithinElement(event, triggerElement) || closePopoverIfTriggerClicked;
      if (isNotWithinPopover && isNotWithinTrigger) {
        onClose(event);
      }
    }
  }, [closePopoverIfTriggerClicked, onClose, popoverElement, triggerElement]);

  /**
   * Handles keyboard KeyDown input when the popover surface is in focus.
   */
  const handleKeyDown = useCallback(event => {
    const {
      key
    } = event;
    if (popoverElement) {
      // Trap the focus of the browser inside the popover element. Keep the user in this element
      // and allow them to tab through content until the user closes the <Popover />.
      trapTabFocus(event, popoverElement, false);
    }
    if (key === KEYBOARD_KEYNAMES.SPACE) {
      event.stopPropagation();
    }
    if (isFunction(onKeyDown)) {
      onKeyDown(event);
    }
  }, [onKeyDown, popoverElement]);

  /**
   * Function called once the <Transition /> component is in the entered state.
   *
   * If we are showing focus because we have had some keyboard input, we should set
   * focus to the Popover element.
   */
  const handlePopoverEntered = useCallback(() => {
    if (popoverElement) {
      if (popoverElementDidMount) {
        popoverElementDidMount(popoverElement);
      } else {
        focusFirstElement(popoverElement);
      }
    }
  }, [popoverElementDidMount, popoverElement]);
  const handlePopoverEntering = useCallback(isAppearing => {
    if (!isAppearing && update !== null) {
      update();
    }
  }, [update]);

  /**
   * Function called once the <Transition /> component is in the exited state.
   */
  const handlePopoverExited = useCallback(() => {
    if (focusFirstElementOnPopoverExited && triggerElement) {
      focusFirstElement(triggerElement);
    }
  }, [focusFirstElementOnPopoverExited, triggerElement]);

  // change of $minWidth means the position of the trigger might also changed
  useLayoutEffect(() => {
    if (update) {
      update();
    }
  }, [$minWidth, update]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, trigger({
    setTriggerElement
  }), /*#__PURE__*/React__default.createElement(PopoverContainer, _extends({}, listboxProps, {
    focusableBody: true,
    attributes: attributes,
    disablePortal: false,
    handleKeyDown: handleKeyDown,
    handlePopoverEntered: handlePopoverEntered,
    handlePopoverEntering: handlePopoverEntering,
    handlePopoverExited: handlePopoverExited,
    isOpen: isOpen,
    onDocumentMousedown: handleDocumentMousedown,
    position: "bottom",
    setPopoverElement: setPopoverElement,
    styles: styles,
    variant: "default",
    popoverRef: popoverRef,
    shadowRoot: shadowRoot,
    transitionDuration: 100,
    popoverType: "Dropdown"
  }), /*#__PURE__*/React__default.createElement(PopoverContentContainerWrapper, {
    $minWidth: $minWidth,
    $fullWidth: $fullWidth
  }, /*#__PURE__*/React__default.createElement(PopoverContentContainer, {
    handleCloseClicked: noop,
    padding: "none",
    variant: "default",
    withCloseButton: false
  }, /*#__PURE__*/React__default.createElement(OverflowWrapper, {
    direction: "column",
    alignItems: "inherit",
    $maxHeight: maxHeight
  }, children)))));
};

export { DropdownPopover as D };
//# sourceMappingURL=DropdownPopover-a65OT6uW.js.map
